import { computed, inject, Injectable } from '@angular/core';
import { BalanceStore } from '../data';
import { BalanceApiService } from '@infrastructure/api/balance';
import { IBalance } from '@domain/balance';

@Injectable({ providedIn: 'root' })
export class BalanceFacade {
  readonly #store = inject(BalanceStore);
  readonly #api = inject(BalanceApiService);
  #lastLoggedIsFirstExternalBalance: boolean | undefined = undefined;

  readonly vm = computed(() => ({
    balance: this.#store.balance(),
    isFirstExternalBalance: this.#store.isFirstExternalBalance(),
    loading: this.#store.loading(),
    error: this.#store.error(),
  }));

  public loadBalance() {
    this.#store.setLoading(true);
    this.#api.getBalance().subscribe({
      next: balance => {
        const rawIsFirstExternalBalance = (balance as any).is_first_external_balance as boolean | undefined;
        const updatedAt = ((balance as any).updated_at ?? (balance as any).updated) as string | undefined;

        // Debug helper: keep the last payload accessible from DevTools without spamming logs.
        try {
          (globalThis as any).__PG_LAST_BALANCE__ = balance;
        } catch {}

        if (
          this.#lastLoggedIsFirstExternalBalance === undefined ||
          this.#lastLoggedIsFirstExternalBalance !== rawIsFirstExternalBalance
        ) {
          console.info('[PG] /balance is_first_external_balance', {
            value: rawIsFirstExternalBalance,
            stars_balance: (balance as any).stars_balance,
            crypto_stars_balance: (balance as any).crypto_stars_balance,
            updated: updatedAt,
          });
          this.#lastLoggedIsFirstExternalBalance = rawIsFirstExternalBalance;
        }

        this.#store.setBalance(balance.stars_balance + balance.crypto_stars_balance);
        this.#store.setIsFirstExternalBalance(!!balance.is_first_external_balance);
        this.#store.setLoading(false);
      },
      error: error => {
        this.#store.setError(error);
        this.#store.setLoading(false);
      },
    });
  }

  public applyRealtimeBalance(balance: Partial<IBalance>): void {
    const starsBalance = typeof balance.stars_balance === 'number' ? balance.stars_balance : 0;
    const cryptoStarsBalance = typeof balance.crypto_stars_balance === 'number' ? balance.crypto_stars_balance : 0;
    this.#store.setBalance(starsBalance + cryptoStarsBalance);

    if (typeof balance.is_first_external_balance === 'boolean') {
      this.#store.setIsFirstExternalBalance(balance.is_first_external_balance);
    }
  }
}
